import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { ApiBaseUrlContext } from '../App';
import { useNavigate } from 'react-router-dom';
import BoxItem from './BoxItem';
import { ReactComponent as FilterIcon } from '../assets/svg/icon-filter.svg';
import { ReactComponent as AddIcon } from '../assets/svg/icon-add.svg';
import { ReactComponent as SectionToggleArrow } from '../assets/svg/icon-toggle-section-arrow.svg';
import { ReactComponent as RemoveItemIcon } from '../assets/svg/icon-remove-1.svg';
import { useTranslation } from 'react-i18next';
import { formatDateByLocale } from '../utils/dateUtils';
/*import './MaintenanceSchedule.css';*/
import '../boxitem.css';
import '../topmenu.css';
import '../pageSearch.css';
import '../forms.css';
import GroupedDataTable from './GroupDataTable2'; 


const InventoryList = () => {
    const [showForm, setShowForm] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showContent, setShowContent] = useState(true);
    const { t, i18n } = useTranslation();

    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        manufacturer: '',
        location: '',
    });
    const [dropdownOptions, setDropdownOptions] = useState({
        statuses: ['CHECKED_IN', 'CHECKED_OUT'], //Added 20250221 BO
        //statuses: [t('inventory.checkedIn'), t('inventory.checkedOut')],Removed 20250221 BO
        categories: [],
        manufacturers: [],
        locations: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiBaseUrl = useContext(ApiBaseUrlContext);
    const navigate = useNavigate();

    // Group items by urgency status
    const groupedItems = {
        red: [],
        blue: [],
        green: [],
        gray: [],
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setFilters({
          status: '',
          category: '',
          manufacturer: '',
          location: '',
        });
        setFilteredItems(items); // Reset filtered items to the full list
      };

    filteredItems.forEach((item) => {
        groupedItems[item.backgroundColor]?.push(item);
    });

    useEffect(() => {
        const fetchItems = async () => {
          try {
            setLoading(true);
            const response = await axios.get(`${apiBaseUrl}/mnt/api/inventory`);
            const updatedItems = response.data.map((item) => {
              let isCheckedOut = false;
              let checkedOutBy = null; // New property for storing the username
              let expectedReturnDateFormatted = null; 
              if (item.checkin_checkout_log) {
                try {
                  const log = JSON.parse(item.checkin_checkout_log);
                  if (log.length > 0) {
                    const lastLog = log[log.length - 1];
                    isCheckedOut = lastLog.description === 'CHECKED_OUT'; // Added 20250221 BO
                    // isCheckedOut = lastLog.description === 'Checked Out'; Removed 20250221 BO
                    // If the item is checked out, store who did it
                    if (isCheckedOut) {
                        if (lastLog.userfirstname && lastLog.userlastname) {
                          checkedOutBy = `${lastLog.userfirstname} ${lastLog.userlastname}`;
                        } else if (lastLog.checkout_by) {
                          checkedOutBy = lastLog.checkout_by;
                        } else {
                          checkedOutBy = 'Unknown User'; // Fallback if all fields are missing
                        }
                      
                        if (lastLog.expectedReturnDate) {
                          const date = new Date(lastLog.expectedReturnDate);
                          expectedReturnDateFormatted = date.toLocaleDateString();
                        }
                      }
                      
                  }
                } catch (e) {
                  console.error('Error parsing checkin_checkout_log for item:', item.item_id, e);
                }
              }
      
              const calculateUrgency = (timestamp) => {
                if (!timestamp) return Infinity; // No date set, treat as least urgent
                const now = new Date().getTime();
                return Math.ceil((timestamp - now) / 86400000); // Difference in days
              };
      
              const nextMaintenanceUrgency = calculateUrgency(parseInt(item.next_maintenance));
              const nextCalibrationUrgency = calculateUrgency(parseInt(item.next_calibration));
              const mostUrgent = Math.min(nextMaintenanceUrgency, nextCalibrationUrgency);
      
              return {
                ...item,
                isCheckedOut,
                checkedOutBy, // Added property
                expectedReturnDateFormatted, 
                nextMaintenanceFormatted: item.next_maintenance
                  ? new Date(parseInt(item.next_maintenance, 10)).toLocaleDateString()
                  : null,
                nextCalibrationFormatted: item.next_calibration
                  ? new Date(parseInt(item.next_calibration, 10)).toLocaleDateString()
                  : null,
                mostUrgent, // Use for sorting
                backgroundColor: mostUrgent <= 1
                  ? 'red'
                  : mostUrgent <= 7
                  ? 'blue'
                  : Number.isInteger(mostUrgent)
                  ? 'green'
                  : 'gray', // Assign background color based on urgency
              };
            });
      
            // Sort items by urgency
            const sortedItems = updatedItems.sort((a, b) => a.mostUrgent - b.mostUrgent);
      
            setItems(sortedItems);
            setFilteredItems(sortedItems);
      
            const categories = Array.from(new Set(updatedItems.map((item) => item.category).filter(Boolean)));
            const manufacturers = Array.from(new Set(updatedItems.map((item) => item.manufacturer).filter(Boolean)));
            const locations = Array.from(new Set(updatedItems.map((item) => item.assigned_location).filter(Boolean)));
      
            setDropdownOptions({
              statuses: ['CHECKED_IN', 'CHECKED_OUT'], // Language-agnostic values  Added 20250221 BO
              // statuses: ['Checked In', 'Checked Out'], Removed 20250221 BO
              categories,
              manufacturers,
              locations,
            });
          } catch (error) {
            setError('Error fetching items');
            console.error('Error fetching items:', error);
          } finally {
            setLoading(false);
          }
        };
      
        fetchItems();
      }, [apiBaseUrl]);

    useEffect(() => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        const filtered = items.filter((item) => {
            const matchesSearch = (
                item.item_id.toString().includes(lowercasedSearchTerm) ||
                (item.item_name || '').toLowerCase().includes(lowercasedSearchTerm) ||
                (item.item_type || '').toLowerCase().includes(lowercasedSearchTerm) ||
                (item.category || '').toLowerCase().includes(lowercasedSearchTerm) ||
                (item.model_number || '').toLowerCase().includes(lowercasedSearchTerm) ||
                (item.manufacturer || '').toLowerCase().includes(lowercasedSearchTerm) ||
                (item.assigned_location || '').toLowerCase().includes(lowercasedSearchTerm) ||  // ✅ Fixed line
                (item.checkedOutBy || '').toLowerCase().includes(lowercasedSearchTerm) ||  // ✅ Added line
                (filters.status && filters.status.toLowerCase().includes(lowercasedSearchTerm)) // ✅ Fixed line
            );

            const matchesStatus =
                !filters.status || 
                (filters.status === 'CHECKED_OUT' && item.isCheckedOut) || //Added 20250221 BO
                (filters.status === 'CHECKED_IN' && !item.isCheckedOut); //Added 20250221 BO
                //(filters.status === t('inventory.checkedOut') && item.isCheckedOut) || Removed 20250221 BO
                //(filters.status === t('inventory.checkedIn') && !item.isCheckedOut);

            const matchesCategory = !filters.category || item.category === filters.category;
            const matchesManufacturer = !filters.manufacturer || item.manufacturer === filters.manufacturer;
            const matchesLocation = !filters.location || item.assigned_location === filters.location;

            return matchesSearch && matchesStatus && matchesCategory && matchesManufacturer && matchesLocation;
        });

        setFilteredItems(filtered);
    }, [searchTerm, filters, items]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (filterKey, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [filterKey]: value,
        }));
    };

    const handleDelete = async (id) => {
        // Ask for confirmation before deleting
        const confirmed = window.confirm("Are you sure you want to delete this item?");
        if (!confirmed) return; // Abort if the user cancels
      
        try {
          await axios.delete(`${apiBaseUrl}/mnt/api/inventory/${id}`);
          const updatedItems = items.filter((item) => item.item_id !== id);
          setItems(updatedItems);
          setFilteredItems(updatedItems);
        } catch (error) {
          setError('Error deleting item');
          console.error('Error deleting item:', error);
        }
      };
      

    const handleEdit = (id) => {
        navigate(`/inventory/edit/${id}`);
    };

    const handleLogAction = (id, logType) => {
        navigate(`/inventory/${id}/logs/${logType}`);
    };

    const toggleDropdown = (index) => {
        setFilteredItems((prevItems) =>
            prevItems.map((item, i) => ({
                ...item,
                dropdownOpen: i === index ? !item.dropdownOpen : false,
            }))
        );
    };

const addColor = (items, color) =>
  items.map((i) => ({ ...i, _groupKey: color, _color: i.backgroundColor || color }));

const groupedData = Object.fromEntries(
  Object.entries(groupedItems).map(([color, items]) => [
    t(`inventory.header.${color}`),
    addColor(items, color),
  ])
);

    return (

        <>

        {showFilter && (

            <div>
              
                <div className="filter-container" onClick={() => setShowFilter(!showFilter)}>
                    <div className="filter" onClick={(e) => e.stopPropagation()}>
                        <div className="filter__fields">
                            
                            <label id="filter-search" className="filter__label">{t(`inventory.FilterLabelFreeSearch`)}</label>
                            <div className="entry-item">
                                <input
                                    id="search"
                                    type="text"
                                    placeholder={t(`inventory.Search`)}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />  
                            </div>                         

                            <label id="filter-status" className="filter__label">{t(`inventory.FilterLabelStatus`)}</label>
                            <div className="entry-item">
                                <select id="filter-status" 
                                className="form-select-drop"
                                onChange={(e) => handleFilterChange('status', e.target.value)}>
                                    <option value="">{t(`inventory.FilterAllStatuses`)}</option>
                                    {dropdownOptions.statuses.map((status) => (
                                        <option key={status} value={status}>
                                            {status === 'CHECKED_IN' ? t('inventory.checkedIn') : t('inventory.checkedOut')}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <label id="filter-status" className="filter__label">{t(`inventory.FilterLabelCategory`)}</label>
                            <div id="filter-category" className="entry-item">
                                <select id="filter-category" 
                                className="form-select-drop"
                                onChange={(e) => handleFilterChange('category', e.target.value)}>
                                    <option value="">{t(`inventory.FilterAllCategories`)}</option>
                                    {dropdownOptions.categories.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <label id="filter-manufacturers" className="filter__label">{t(`inventory.FilterLabelManufacturer`)}</label>
                            <div id="filter-manufacturers" className="entry-item">
                                <select 
                                    className="form-select-drop"
                                    onChange={(e) => handleFilterChange('manufacturer', e.target.value)}>
                                    <option value="">{t(`inventory.FilterAllManufacturers`)}</option>
                                    {dropdownOptions.manufacturers.map((manufacturer) => (
                                        <option key={manufacturer} value={manufacturer}>
                                            {manufacturer}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <label id="filter-locations" className="filter__label">{t(`inventory.FilterLabelLocation`)}</label>
                            <div id="filter-locations" className="entry-item">
                                <select 
                                    className="form-select-drop"
                                    onChange={(e) => handleFilterChange('location', e.target.value)}>
                                    <option value="">{t(`inventory.FilterAllLocations`)}</option>
                                    {dropdownOptions.locations.map((location) => (
                                        <option key={location} value={location}>
                                            {location}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter__buttons">
                            <button type="button" className="button--grey" onClick={handleResetFilters}>
                                {t(`inventory.FilterReset`)}
                            </button> 
                            </div>  

                        </div>
                    </div>
                </div>
            </div>

        )}

        {showForm && (

            <div>Form</div>

        )}

        {showContent && (

            <div>

                <div className="content">

                    <div className="top-menu">
                        <div className="top-menu__divider"></div>
                        <div className="top-menu__icon-button top-menu__filter-button" onClick={() => setShowFilter(!showFilter)}><FilterIcon /></div>
                        <button type="button" className="button--icon-add button--green" onClick={() => navigate('/inventory/new')}><AddIcon /> {t(`inventory.AddItem`)}</button>
                    </div>

                    <div className="content__main-title">{t(`inventory.HeaderInventoryList`)}</div>

                    {loading && <div className="info-message">Loading inventory items...</div>}
                    {error && <div className="info-message">{error}</div>}

                        <GroupedDataTable
                          groupedData={groupedData}
                          isFiltered={false} // Set to true if you later introduce filtering
                          headers={[
                            {
                              label: t("inventoryForm.labels.itemName"),
                              accessor: "item_name",
                              elasticCell: true,
                              colorStyle: "block",
                              colorValueFrom: (item) => item._color || "grey",
                            },
                            {
                              label: t("inventory.ObjectType"),
                              accessor: "item_type",
                              hideMobile: true,
                            },
                            {
                              label: t("inventory.checkedOutBy"),
                              render: (item) => item.isCheckedOut ? item.checkedOutBy || "-" : "-",
                              hideMobile: true,
                            },
                            {
                              label: t("inventory.expectedReturnDate"),
                              render: (item) => item.expectedReturnDateFormatted || "-",
                              hideMobile: true,
                            },
                            {
                              label: t("inventory.nextMaintenance"),
                              render: (item) =>
                                item.nextMaintenanceFormatted
                                  ? formatDateByLocale(Math.floor(item.next_maintenance / 1000), i18n.language, false)
                                  : "-",
                              hideMobile: true,
                            },
                            {
                              label: t("inventory.nextCalibration"),
                              render: (item) =>
                                item.nextCalibrationFormatted
                                  ? formatDateByLocale(Math.floor(item.next_calibration / 1000), i18n.language, false)
                                  : "-",
                              hideMobile: true,
                            },
                          ]}
                          rowKeyFn={(item) => item.item_id}
                          getPrimaryActionLabel={() => t('inventory.edit')}
                          onPrimaryAction={(item) => handleEdit(item.item_id)}
                          groupMeta={Object.fromEntries(
                              Object.keys(groupedItems).map((color) => [
                                t(`inventory.header.${color}`),
                                {
                                  title: t(`inventory.header.${color}`),
                                  description: t(`inventory.description.${color}`),
                                  emptyMessage: t(`inventory.noItems.${color}`),
                                },
                              ])
                            )}
                        contextualOptionsFn={(item) => [
                          {
                            label: item.isCheckedOut ? t('inventory.checkIn') : t('inventory.checkOut'),
                            onClick: (e) => {
                              e.preventDefault();
                              handleLogAction(item.item_id, 'checkin_checkout_log');
                            },
                          },
                          {
                            label: t('inventory.maintenance'),
                            onClick: (e) => {
                              e.preventDefault();
                              handleLogAction(item.item_id, 'maintenance_history');
                            },
                          },
                          {
                            label: t('inventory.calibrate'),
                            onClick: (e) => {
                              e.preventDefault();
                              handleLogAction(item.item_id, 'calibration_history');
                            },
                          },
                          {
                            label: t('inventory.edit'),
                            onClick: (e) => {
                              e.preventDefault();
                              handleEdit(item.item_id);
                            },
                          },
                          {
                            label: t('inventory.delete'),
                            onClick: (e) => {
                              e.preventDefault();
                              handleDelete(item.item_id);
                            },
                          },
                        ]}
                        />

                        {/*

                    {Object.entries(groupedItems).map(([color, items]) => (
                        <div key={color} className={`content__section inventory-group inventory-group-${color}`}>

                            <div className="content__section-header">
                                <div className="content__section-title">
                                    {t(`inventory.header.${color}`)}
                                    <div className="content__section-title-description">
                                        {t(`inventory.description.${color}`)}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="content__machine-list">

                            {items.length > 0 ? (
                                items.map((item) => (
                                    <div key={item.item_id}>
                                        <BoxItem
                                            color={item.backgroundColor}
                                            title={item.item_name}
                                            infoVisible={{
                                                [t('inventory.ObjectType')]: item.item_type,
                                                ...(item.isCheckedOut && item.checkedOutBy ? { [t('inventory.checkedOutBy')]: item.checkedOutBy } : {}),
                                                ...(item.expectedReturnDateFormatted
                                                    ? { [t('inventory.expectedReturnDate')]: item.expectedReturnDateFormatted }
                                                    : {}),
                                                ...(item.nextMaintenanceFormatted && {
                                                  [t('inventory.nextMaintenance')]: formatDateByLocale(
                                                    Math.floor(item.next_maintenance / 1000),
                                                    i18n.language, false
                                                  ),
                                                }),
                                                ...(item.nextCalibrationFormatted && {
                                                  [t('inventory.nextCalibration')]: formatDateByLocale(
                                                    Math.floor(item.next_calibration / 1000),
                                                    i18n.language, false
                                                  ),
                                                }),
                                              }}
                                            buttonAction={{
                                                name: t('inventory.edit'),
                                                handler: () => handleEdit(item.item_id),
                                            }}
                                              options={{
                                                [item.isCheckedOut ? t('inventory.checkIn') : t('inventory.checkOut')]: () => handleLogAction(item.item_id, 'checkin_checkout_log'),
                                                [t('inventory.maintenance')]: () => handleLogAction(item.item_id, 'maintenance_history'),
                                                [t('inventory.calibrate')]: () => handleLogAction(item.item_id, 'calibration_history'),
                                                [t('inventory.delete')]: () => handleDelete(item.item_id),
                                              }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="content__section-title-description">{t(`inventory.noItems.${color}`)}</div>
                            )}

                            </div>

                        </div>
                    ))}

*/}
                </div>

            </div>

        )}

        </>
    );
};

export default InventoryList;